import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import i18n from '../i18n';
import { useTeamStore } from '../stores/useTeamStore';
import { useUserStore } from '../stores/useUserStore';
import { useImageStore } from '../stores/useImageStore';
import { useVideoStore } from '../stores/useVideoStore';
import { useNotificationStore } from '../stores/useNotificationStore';
import { useInAppNotificationStore } from '../stores/useInAppNotificationStore';
import { useSubscriptionLimitsStore } from '../stores/useSubscriptionLimitsStore';

import { getInitData, getAuthType } from '../utils/authHeaders';

// Get base URL for WebSocket connection
const getBaseUrl = () => {
  if (import.meta.env.DEV) {
    return 'http://localhost:3000';
  }
  return window.location.origin;
};

// Online user info
interface OnlineUser {
  odUserId: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
}

interface UseWebSocketOptions {
  enabled?: boolean;
  onTokensUpdated?: (data: any) => void;
  onOnlineUsersChange?: (users: OnlineUser[]) => void;
  onTeamChatCreated?: (data: any) => void;
  onTeamChatDeleted?: (data: any) => void;
  onTeamChatMessage?: (data: any) => void;
}

/**
 * Hook for subscribing to real-time events via WebSocket
 *
 * Features:
 * - Personal token balance updates (chat, generation, payment, subscription)
 * - Team: online presence, chat events (create/delete/message)
 * - Auto-reconnection with exponential backoff
 *
 * Connects for ALL authenticated users (personal notifications via user:{id} room).
 * Team features activate when teamId is provided.
 *
 * @param options - Configuration options
 * @returns Connection status and methods
 */
export type { OnlineUser };

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const { enabled = true } = options;
  
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const currentTeamIdRef = useRef<string | null>(null);
  
  // State for UI
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);

  const { activeTeam, updateTeamTokens, fetchMembers, fetchFolders, toggleTeamMode } = useTeamStore();
  const updateUserTokens = useUserStore(state => state.updateTokens);
  const updateUserSubscription = useUserStore(state => state.updateSubscription);
  const { addNotification } = useNotificationStore();

  const removeImage = useImageStore(state => state.removeImage);
  const removeVideo = useVideoStore(state => state.removeVideo);

  // Use refs for values that change frequently to avoid reconnections
  const activeTeamRef = useRef(activeTeam);
  const optionsRef = useRef(options);

  // Update refs when values change
  activeTeamRef.current = activeTeam;
  optionsRef.current = options;

  const MAX_RECONNECT_ATTEMPTS = 10;
  const RECONNECT_DELAY_BASE = 1000;

  /**
   * Initialize WebSocket connection
   */
  const connect = useCallback(() => {
    // Get auth data for authentication
    const initData = getInitData();

    // Don't connect if auth data is not available
    if (!initData) {
      return;
    }

    // Don't connect if already connected or connecting
    if (socketRef.current?.connected || socketRef.current?.active) {
      return;
    }

    // Clean up existing socket if any (but not connected)
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current = null;
    }

    const baseUrl = getBaseUrl();

    // Determine auth type to pass correct params
    // For session auth (Google/Yandex) pass sessionId, for Telegram pass initData
    const authType = getAuthType();
    const isSessionAuth = authType === 'session';
    const authParams = isSessionAuth
      ? { sessionId: initData }
      : { initData };

    // Create Socket.io connection
    const socket = io(baseUrl, {
      path: '/ws/teams',
      auth: authParams,
      query: authParams,
      transports: ['websocket', 'polling'],
      reconnection: false, // We handle reconnection manually
      timeout: 10000
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      setIsConnected(true);
      reconnectAttempts.current = 0;
      
      // Join team room if active team exists
      const team = activeTeamRef.current;
      if (team?.id) {
        socket.emit('join_team', { teamId: team.id });
        currentTeamIdRef.current = team.id;
      }
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      setOnlineUsers([]);
      currentTeamIdRef.current = null;
      
      // Auto-reconnect for certain disconnect reasons
      if (reason !== 'io client disconnect') {
        scheduleReconnect();
      }
    });

    socket.on('connect_error', () => {
      setIsConnected(false);
      scheduleReconnect();
    });

    // Team state event (initial state when joining)
    socket.on('team_state', (data: { teamId: string; onlineUsers: OnlineUser[] }) => {
      setOnlineUsers(data.onlineUsers);
      optionsRef.current.onOnlineUsersChange?.(data.onlineUsers);
    });

    // Member online/offline events
    socket.on('MEMBER_ONLINE', (data) => {
      setOnlineUsers(prev => {
        // Avoid duplicates
        if (prev.some(u => u.odUserId === data.userId)) return prev;
        const newUser: OnlineUser = {
          odUserId: data.userId,
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          photoUrl: data.photoUrl
        };
        const updated = [...prev, newUser];
        optionsRef.current.onOnlineUsersChange?.(updated);
        return updated;
      });
    });

    socket.on('MEMBER_OFFLINE', (data) => {
      setOnlineUsers(prev => {
        const updated = prev.filter(u => u.odUserId !== data.userId);
        optionsRef.current.onOnlineUsersChange?.(updated);
        return updated;
      });
    });

    // Generation deleted (by any team member)
    socket.on('GENERATION_DELETED', (data) => {
      // Remove from stores immediately
      if (data.generationType === 'image') {
        removeImage(data.generationId);
      } else if (data.generationType === 'video') {
        removeVideo(data.generationId);
      }
      
      // Show notification (optional, only if it's another user)
      // const currentUserId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString();
      // if (data.userId && data.userId !== currentUserId) {
      //   addNotification(`${data.userName || 'Участник'} удалил ${data.generationType === 'image' ? 'изображение' : 'видео'}`, 'info');
      // }
    });

    // Tokens updated (personal + team)
    socket.on('TOKENS_UPDATED', (data) => {
      if (data.reason === 'chat' || data.reason === 'generation' || data.reason === 'generation_refund' ||
          data.reason === 'payment' || data.reason === 'subscription' || data.reason === 'refund' || data.reason === 'admin') {
        // Personal balance update
        if (data.newBalance !== undefined) {
          updateUserTokens(data.newBalance);
        }
      }
      if (activeTeamRef.current) {
        updateTeamTokens(activeTeamRef.current.id, data.newBalance);
      }

      // External balance changes invalidate the cached subscription limits snapshot
      // (max tokens, sub tier, drip schedule). Local 'chat'/'generation' usage is
      // already covered by incrementUsageOptimistic on the originating page.
      const externalReasons = new Set([
        'payment', 'subscription', 'admin', 'refund', 'generation_refund',
        'seat_upgrade', 'period_upgrade', 'renewal', 'team_subscription'
      ]);
      if (externalReasons.has(data.reason)) {
        useSubscriptionLimitsStore.getState().fetchLimits(true).catch(() => {});
      }

      optionsRef.current.onTokensUpdated?.(data);
    });

    // Account merge events (personal)
    socket.on('MERGE_COMPLETED', (data) => {
      window.dispatchEvent(new CustomEvent('MERGE_COMPLETED', { detail: data }));
    });
    socket.on('MERGE_REJECTED', (data) => {
      window.dispatchEvent(new CustomEvent('MERGE_REJECTED', { detail: data }));
    });

    // Subscription updated (personal)
    socket.on('SUBSCRIPTION_UPDATED', (data) => {
      if (data.tier) {
        updateUserSubscription({
          tier: data.tier,
          period: data.period || null,
          status: data.status || 'active'
        });
      }
      // Update tokens if included
      if (data.tokens !== undefined && data.tokens !== null) {
        updateUserTokens(data.tokens);
      }
    });

    // Member joined/left team (permanent, not online status)
    socket.on('MEMBER_JOINED', (data) => {
      addNotification(
        i18n.t('team:notify.memberJoined', { name: data.firstName || i18n.t('team:notify.defaultNewMember') }),
        'info'
      );
      if (activeTeamRef.current) {
        fetchMembers(activeTeamRef.current.id);
      }
    });

    socket.on('MEMBER_LEFT', (data) => {
      // Проверяем, не кикнули ли текущего пользователя
      const currentUserId = useUserStore.getState().user?.telegramId?.toString()
        || useUserStore.getState().user?.id;
      if (data.userId && currentUserId && data.userId === currentUserId) {
        // Текущего пользователя удалили из команды — сбрасываем team mode + reload
        addNotification(
          i18n.t('team:notify.youWereRemoved', { defaultValue: 'Вы были удалены из команды' }),
          'warning'
        );
        toggleTeamMode(false);
        setTimeout(() => { window.location.href = '/chat'; }, 1500);
        return;
      }
      addNotification(
        i18n.t('team:notify.memberLeft', { name: data.firstName || i18n.t('team:notify.defaultMember') }),
        'info'
      );
      if (activeTeamRef.current) {
        fetchMembers(activeTeamRef.current.id);
      }
    });

    socket.on('TEAM_DELETED', (data) => {
      // Команда удалена — сбрасываем team mode + reload
      addNotification(
        i18n.t('team:notify.teamDeleted', {
          name: data.teamName,
          defaultValue: `Команда "${data.teamName || ''}" была удалена`
        }),
        'warning'
      );
      toggleTeamMode(false);
      setTimeout(() => { window.location.href = '/chat'; }, 1500);
    });

    // Folder events
    socket.on('FOLDER_CREATED', (data) => {
      if (activeTeamRef.current && data.folderType) {
        fetchFolders(activeTeamRef.current.id, data.folderType);
      }
    });

    socket.on('FOLDER_DELETED', (data) => {
      if (activeTeamRef.current && data.folderType) {
        fetchFolders(activeTeamRef.current.id, data.folderType);
      }
    });

    // Team chat events
    socket.on('TEAM_CHAT_CREATED', (data) => {
      optionsRef.current.onTeamChatCreated?.(data);
    });

    socket.on('TEAM_CHAT_DELETED', (data) => {
      optionsRef.current.onTeamChatDeleted?.(data);
    });

    socket.on('TEAM_CHAT_MESSAGE', (data) => {
      optionsRef.current.onTeamChatMessage?.(data);
    });

    // In-app notification pushed by admin broadcast — prepend to store without refresh.
    // The WS payload carries title/description as RU fallback strings plus
    // separate titleLocalized/descriptionLocalized objects (see notifications.routes.js).
    // Fold those back into title/description as Localized-shaped values so the
    // popup picks the right language via pickLocalized. Also pass through
    // template / templateData / displayContext / captions / localizedMedia
    // unchanged — they're already in the shape useNotificationLanguage expects.
    socket.on('IN_APP_NOTIFICATION', (data) => {
      try {
        const existing = useInAppNotificationStore.getState().notifications;
        if (existing.some(n => n.id === data.id)) return; // дедуп
        const { titleLocalized, descriptionLocalized, ...rest } = data || {};
        const normalized = {
          ...rest,
          title: titleLocalized || rest.title,
          description: descriptionLocalized || rest.description,
        };
        useInAppNotificationStore.getState().addNotification(normalized);
      } catch {
        // no-op
      }
    });

    // Tool job events (upscale, image_edit) — dispatch as CustomEvents
    // so tool-specific hooks (useUpscaleActions, useImageEditActions) can listen
    socket.on('IMAGE_JOB_COMPLETED', (data) => {
      window.dispatchEvent(new CustomEvent('IMAGE_JOB_COMPLETED', { detail: data }));
    });

    socket.on('IMAGE_JOB_FAILED', (data) => {
      window.dispatchEvent(new CustomEvent('IMAGE_JOB_FAILED', { detail: data }));
    });

    socket.on('IMAGE_JOB_PROGRESS', (data) => {
      window.dispatchEvent(new CustomEvent('IMAGE_JOB_PROGRESS', { detail: data }));
    });

  }, [updateTeamTokens, updateUserTokens, updateUserSubscription, fetchMembers, fetchFolders, addNotification,
      removeImage, removeVideo]);

  /**
   * Schedule reconnection with exponential backoff
   */
  const scheduleReconnect = useCallback(() => {
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    const delay = RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttempts.current);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttempts.current++;
      connect();
    }, delay);
  }, [connect]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      // Only disconnect if connected or connecting to avoid "closed before established" warning
      const socket = socketRef.current;
      socketRef.current = null;
      
      // Remove all listeners first to prevent memory leaks
      socket.removeAllListeners();
      
      // Check if socket is in a state where disconnect is meaningful
      if (socket.connected || socket.active) {
        socket.disconnect();
      }
    }

    // Clear state
    setIsConnected(false);
    setOnlineUsers([]);
    reconnectAttempts.current = 0;
    currentTeamIdRef.current = null;
  }, []);

  /**
   * Join a team room
   */
  const joinTeam = useCallback((teamId: string) => {
    if (socketRef.current?.connected) {
      // Leave previous team if any
      if (currentTeamIdRef.current && currentTeamIdRef.current !== teamId) {
        socketRef.current.emit('leave_team', { teamId: currentTeamIdRef.current });
      }
      
      socketRef.current.emit('join_team', { teamId });
      currentTeamIdRef.current = teamId;
    }
  }, []);

  /**
   * Leave a team room
   */
  const leaveTeam = useCallback((teamId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_team', { teamId });
      if (currentTeamIdRef.current === teamId) {
        currentTeamIdRef.current = null;
        setOnlineUsers([]);
      }
    }
  }, []);

  // Connect when enabled — for ALL authenticated users (personal notifications)
  // Team features auto-activate when activeTeam is set
  useEffect(() => {
    let connectTimeout: ReturnType<typeof setTimeout> | null = null;

    if (enabled) {
      // Small delay to avoid rapid connect/disconnect on initial mount
      connectTimeout = setTimeout(() => {
        connect();
      }, 100);
    } else {
      disconnect();
    }

    return () => {
      if (connectTimeout) {
        clearTimeout(connectTimeout);
      }
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Handle team change (join new team room when team changes)
  useEffect(() => {
    if (socketRef.current?.connected && activeTeam?.id) {
      joinTeam(activeTeam.id);
    }
  }, [activeTeam?.id, joinTeam]);

  // Reconnect when page becomes visible or gets focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (enabled && !socketRef.current?.connected) {
          connect();
        }
      }
    };

    const handleFocus = () => {
      if (enabled && !socketRef.current?.connected) {
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, connect]);

  return {
    isConnected,
    onlineUsers,
    connect,
    disconnect,
    joinTeam,
    leaveTeam
  };
};

export default useWebSocket;

